import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { TFieldSchema } from '../schemas/payment-provider-schema';

type ProviderFieldsFormProps = {
  schema: TFieldSchema[];
  values: Record<string, string>;
  encryptedKeys?: string[];
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
  readOnly?: boolean;
};

const MASK = '••••••••';

const formatLabel = (id: string) =>
  id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const sortBySchemaOrder = (fields: TFieldSchema[]) =>
  [...fields].sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

const ProviderFieldsForm = ({
  schema,
  values,
  encryptedKeys = [],
  onChange,
  disabled = false,
  readOnly = false,
}: ProviderFieldsFormProps) => {
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [touchedSecrets, setTouchedSecrets] = useState<Record<string, boolean>>({});

  const encryptedKeysSignature = encryptedKeys.join('|');
  useEffect(() => {
    setTouchedSecrets({});
  }, [encryptedKeysSignature]);

  if (!schema || schema.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>No fields defined for this scope yet.</Typography>
      </Box>
    );
  }

  const handleFieldChange = (id: string, value: string) => {
    if (readOnly) return;
    onChange({ ...values, [id]: value });
    if (encryptedKeys.includes(id)) {
      setTouchedSecrets((prev) => ({ ...prev, [id]: true }));
    }
  };

  return (
    <Box sx={styles.formBox}>
      {sortBySchemaOrder(schema).map((field) => {
        const isSecret = field.type === 'secret' || encryptedKeys.includes(field.id);
        const stored = values[field.id] ?? '';
        const masked = isSecret && stored === MASK && !touchedSecrets[field.id];
        const visible = visibleSecrets[field.id] || !isSecret;

        return (
          <Box key={field.id} sx={styles.fieldRow}>
            <TextField
              fullWidth
              label={`${field.label || formatLabel(field.id)}${field.required ? ' *' : ''}`}
              type={isSecret && !visible ? 'password' : 'text'}
              value={masked ? '' : stored}
              placeholder={masked ? MASK : undefined}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              disabled={disabled || readOnly}
              size="small"
              helperText={
                isSecret
                  ? masked
                    ? readOnly
                      ? 'Stored encrypted.'
                      : 'Stored encrypted. Type a new value to replace, or leave blank to keep.'
                    : readOnly
                      ? undefined
                      : 'This value will be encrypted on save.'
                  : undefined
              }
              InputProps={
                isSecret
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            tabIndex={-1}
                            onClick={() =>
                              setVisibleSecrets((prev) => ({
                                ...prev,
                                [field.id]: !prev[field.id],
                              }))
                            }
                          >
                            {visible ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : undefined
              }
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default ProviderFieldsForm;

const styles = {
  formBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  fieldRow: {
    width: '100%',
  },
  emptyContainer: {
    py: 3,
    textAlign: 'center' as const,
  },
  emptyText: {
    color: '#888',
    fontFamily: 'GilroyMedium',
    fontSize: 14,
  },
};
